import type { HttpTransport } from '@mangovoice/api-client'
import type { PaginatedApiResult } from '@mangovoice/types'
import type { components } from '@mangovoice/types/generated/mango-api'

import { stringifyParams } from './utils'

export type SoundClip = components['schemas']['SoundClip']

/**
 * Sound Clips resource - delegates to the @mangovoice/soundclips-ui library
 * for actual endpoint definitions when used via the domain library adapter.
 * Also exposes direct endpoint methods.
 */
export function createSoundClipsResource(transport: HttpTransport) {
  return {
    getSoundClips: (params?: {
      pbx_id?: number
      limit?: number
      offset?: number
    }) => {
      const qs = stringifyParams((params ?? {}) as Record<string, unknown>)
      return transport.get<PaginatedApiResult<SoundClip>>(
        qs ? `/app/v1/sound-clips/?${qs}` : '/app/v1/sound-clips/',
      )
    },

    getSoundClip: (id: number) =>
      transport.get<SoundClip>(`/app/v1/sound-clips/${id}/`),

    createSoundClip: (
      data: FormData | Partial<SoundClip>,
      config?: { headers?: Record<string, string> },
    ) => transport.post<SoundClip>('/app/v1/sound-clips/', data, config),

    updateSoundClip: (
      id: number,
      data: FormData | Partial<SoundClip>,
      config?: { headers?: Record<string, string> },
    ) => transport.patch<SoundClip>(`/app/v1/sound-clips/${id}/`, data, config),

    deleteSoundClip: (id: number) =>
      transport.delete<void>(`/app/v1/sound-clips/${id}/`),

    /** Pass-through transport for @mangovoice/soundclips-ui adapter */
    transport: {
      get: <T>(url: string, params?: Record<string, unknown>) =>
        transport.get<T>(url, params),
      post: <T>(
        url: string,
        data?: unknown,
        config?: { headers?: Record<string, string> },
      ) => transport.post<T>(url, data, config),
      patch: <T>(
        url: string,
        data?: unknown,
        config?: { headers?: Record<string, string> },
      ) => transport.patch<T>(url, data, config),
      delete: <T>(url: string) => transport.delete<T>(url),
    },
  }
}
